import React from 'react';
import { PRICING } from '../pricing';
import DashboardIcon from '@mui/icons-material/Dashboard';
import QuizIcon from '@mui/icons-material/Quiz';
import CalculateIcon from '@mui/icons-material/Calculate';
import BuildIcon from '@mui/icons-material/Build';

export const frontendSystemDesign = {
    title: "Frontend System Design",
    subtitle: "Architect Level",
    badge: "ADVANCED",
    icon: <DashboardIcon sx={{ fontSize: 40, color: '#ec4899' }} />,
    description: "Architect scalable, high-performance web applications and UI systems.",
    features: [
      "Component Driven Patterns",
      "Performance & Core Web Vitals",
      "Micro-Frontends & Federation",
      "Real-world FAANG UI Cast Studies"
    ],
    price: PRICING.frontendSystemDesign.price,
    mrp: PRICING.frontendSystemDesign.mrp,
    topics: [
      {
        id: "fe_hld_basics",
        title: "Frontend Architecture Core",
        desc: "Foundational concepts for building scalable UI applications.",
        badge: "Fundamentals",
        icon: <CalculateIcon />,
        group: "Architecture Basics",
        problems: [
          { id: "fe_arch_1", title: "Component Driven Architecture & State", badge: "Core", fileUrl: "/documents/frontend_hld/fe_arch_1.md" },
          { id: "fe_arch_2", title: "Networking & Communication Protocols", badge: "Network", fileUrl: "/documents/frontend_hld/fe_arch_2.md" },
          { id: "fe_arch_http", title: "The Mood of HTTP Status Codes", badge: "Web", fileUrl: "/documents/frontend_hld/fe_arch_http.md" },
          { id: "fe_arch_3", title: "Web Performance & Core Web Vitals", badge: "Perf", fileUrl: "/documents/frontend_hld/fe_arch_3.md" },
          { id: "fe_arch_4", title: "Offline Storage & Caching Strategies", badge: "Storage", fileUrl: "/documents/frontend_hld/fe_arch_4.md" },
          { id: "fe_arch_5", title: "Web Security Fundamentals", badge: "Security", fileUrl: "/documents/frontend_hld/fe_arch_5.md" },
          { id: "fe_arch_6", title: "GraphQL & Data Fetching", badge: "API", fileUrl: "/documents/frontend_hld/fe_arch_6.md" },
          { id: "fe_arch_12", title: "Authentication & Authorization", badge: "Auth", fileUrl: "/documents/frontend_hld/fe_arch_12.md" },
          { id: "fe_arch_13", title: "Internationalization (i18n)", badge: "i18n", fileUrl: "/documents/frontend_hld/fe_arch_13.md" },
          { id: "fe_arch_11", title: "Accessibility (a11y) & Inclusivity", badge: "a11y", fileUrl: "/documents/frontend_hld/fe_arch_11.md" }
        ]
      },
      {
        id: "fe_hld_advanced",
        title: "Advanced Architecture Patterns",
        desc: "Master large-scale enterprise frontend patterns and tooling.",
        badge: "Expert",
        icon: <BuildIcon />,
        group: "Advanced Patterns",
        problems: [
          { id: "fe_arch_15", title: "State Machines & Event-Driven UI", badge: "State", fileUrl: "/documents/frontend_hld/fe_arch_15.md" },
          { id: "fe_arch_16", title: "Real-time Systems (WebSockets)", badge: "Real-time", fileUrl: "/documents/frontend_hld/fe_arch_16.md" },
          { id: "fe_arch_17", title: "Error Handling & Observability", badge: "Logs", fileUrl: "/documents/frontend_hld/fe_arch_17.md" },
          { id: "fe_arch_14", title: "SSR vs CSR vs SSG vs ISR", badge: "Render", fileUrl: "/documents/frontend_hld/fe_arch_14.md" },
          { id: "fe_arch_18", title: "SEO Architecture for SPAs", badge: "SEO", fileUrl: "/documents/frontend_hld/fe_arch_18.md" },
          { id: "fe_arch_7", title: "Micro-Frontends & Federation", badge: "Scale", fileUrl: "/documents/frontend_hld/fe_arch_7.md" },
          { id: "fe_arch_8", title: "Design Systems & Theming", badge: "UI/UX", fileUrl: "/documents/frontend_hld/fe_arch_8.md" },
          { id: "fe_arch_9", title: "Build Pipelines & Bundlers", badge: "Build", fileUrl: "/documents/frontend_hld/fe_arch_9.md" },
          { id: "fe_arch_10", title: "Testing Strategies & Automation", badge: "Test", fileUrl: "/documents/frontend_hld/fe_arch_10.md" },
          { id: "fe_arch_19", title: "Performance Budgets & CI/CD", badge: "CI/CD", fileUrl: "/documents/frontend_hld/fe_arch_19.md" },
          { id: "fe_arch_20", title: "Bypassing DOM: WASM & WebGL", badge: "Engine", fileUrl: "/documents/frontend_hld/fe_arch_20.md" },
          { id: "fe_arch_21", title: "Backend-For-Frontend (BFF)", badge: "BFF", fileUrl: "/documents/frontend_hld/fe_arch_21.md" },
          { id: "fe_arch_22", title: "Telemetry & A/B Testing", badge: "Data", fileUrl: "/documents/frontend_hld/fe_arch_22.md" },
          { id: "fe_arch_23", title: "PWA & Deep Offline Sync", badge: "PWA", fileUrl: "/documents/frontend_hld/fe_arch_23.md" },
          { id: "fe_arch_24", title: "WebRTC & SSE Streaming", badge: "Stream", fileUrl: "/documents/frontend_hld/fe_arch_24.md" }
        ]
      },
      {
        id: "fe_hld_case_studies",
        title: "Interview Case Studies",
        desc: "Deep dive into real-world UI system design interview questions.",
        badge: "Practice",
        icon: <DashboardIcon />,
        group: "Case Studies",
        problems: [
          { id: "fe_cs_1", title: "Design an Autocomplete", badge: "Medium", fileUrl: "/documents/frontend_hld/fe_cs_1.md" },
          { id: "fe_cs_2", title: "Design an Image Carousel", badge: "Medium", fileUrl: "/documents/frontend_hld/fe_cs_2.md" },
          { id: "fe_cs_3", title: "Design a News Feed", badge: "Hard", fileUrl: "/documents/frontend_hld/fe_cs_3.md" },
          { id: "fe_cs_4", title: "Design a Chat Application", badge: "Hard", fileUrl: "/documents/frontend_hld/fe_cs_4.md" },
          { id: "fe_cs_5", title: "Design an E-Commerce Page", badge: "Hard", fileUrl: "/documents/frontend_hld/fe_cs_5.md" },
          { id: "fe_cs_6", title: "Design a Collaborative Editor", badge: "Expert", fileUrl: "/documents/frontend_hld/fe_cs_6.md" },
          { id: "fe_cs_7", title: "Design a Video Streaming App", badge: "Expert", fileUrl: "/documents/frontend_hld/fe_cs_7.md" },
          { id: "fe_cs_8", title: "Design a Kanban Board", badge: "Expert", fileUrl: "/documents/frontend_hld/fe_cs_8.md" },
          { id: "fe_cs_9", title: "Design Google Maps", badge: "Expert", fileUrl: "/documents/frontend_hld/fe_cs_9.md" },
          { id: "fe_cs_10", title: "Design a Spreadsheet", badge: "Expert", fileUrl: "/documents/frontend_hld/fe_cs_10.md" }
        ]
      },
      {
        id: "fe_architecture_quizzes",
        title: "Frontend Architecture Quizzes",
        desc: "Consolidated architectural assessment to test your UI design mastery.",
        badge: "Quiz",
        icon: <QuizIcon />,
        group: "Final Assessment",
        problems: [
          { id: "quiz_fe_hld_basics", title: "Architecture Core Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_fe_hld_basics.md" },
          { id: "quiz_fe_hld_advanced", title: "Advanced Patterns Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_fe_hld_advanced.md" },
          { id: "quiz_fe_hld_case_studies", title: "System Design Case Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_fe_hld_case_studies.md" }
        ]
      }
    ]
  };
